import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { type UserRecommendationsGrid_user$key } from './__generated__/UserRecommendationsGrid_user.graphql.ts';
import { type UserRecommendationsItemsGridProps } from './UserRecommendationsItemsGrid.tsx';

const userFragment = graphql`
    fragment UserRecommendationsGrid_user on User
    @argumentDefinitions(
        recommendationType: { type: "UserRecommendationType!", defaultValue: PERSONALIZED }
    ) {
        serviceId
        twoTowerModelItemRecommendations(type: $recommendationType) {
            recommendationIds
        }
    }
`;

const UserRecommendationsItemsGrid = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "UserRecommendationsItemsGrid" */ './UserRecommendationsItemsGrid.tsx'
        )
);

export type UserRecommendationsGridProps = {
    user: UserRecommendationsGrid_user$key | null | undefined;
} & Omit<UserRecommendationsItemsGridProps, 'itemIds' | 'userId'>;

export const UserRecommendationsGrid: FC<UserRecommendationsGridProps> = ({
    user: userRef,
    trackingTrigger,
    isMobile,
    isTablet,
    isTrade,
    priceBookName,
    dataTn,
    title,
    currency,
    titleAlign = 'center',
    autoLoadMore = false,
}) => {
    const user = useFragment(userFragment, userRef);
    const userId = user?.serviceId;
    const recommendationIds = (
        user?.twoTowerModelItemRecommendations?.recommendationIds || []
    ).filter(filterFalsy);
    const hasRecommendations = recommendationIds.length > 0;

    if (!userId || !hasRecommendations) {
        return null;
    }

    return (
        <UserRecommendationsItemsGrid
            itemIds={recommendationIds}
            userId={userId}
            trackingTrigger={trackingTrigger}
            isMobile={isMobile}
            isTablet={isTablet}
            isTrade={isTrade}
            priceBookName={priceBookName}
            dataTn={dataTn}
            title={title}
            currency={currency}
            titleAlign={titleAlign}
            autoLoadMore={autoLoadMore}
        />
    );
};
